import React from "react";
import { Spinner, OverlayTrigger, Tooltip, Badge } from "react-bootstrap";
import { Icon } from "@iconify/react";

const SearchResultsTable = ({
  products,
  loading,
  quantityView,
  onQuantityViewChange,
  dispatchDatesMap,
  onDispatchDateChange,
  onSaveDispatchDate,
  savingDispatchId,
  dispatchDatePermission,
  onProductClick,
  onAddQueue,
  onMediaPreview,
  truncateText,
  formatDate,
  renderDateDifference,
  showCurrentDateDiff,
  statusFilter,
}) => {
  if (loading) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  if (!products || products.length === 0) {
    return (
      <div className="text-center py-4">
        No {statusFilter === 'all' ? '' : statusFilter} products found
      </div>
    );
  }

  return (
    <div className="table-responsive">
      <table className="table table-bordered compact-table" style={{ fontSize: "0.85rem" }}>
        <thead>
          <tr>
            <th className="table-header-text">Order ID</th>
            <th className="table-header-text">Product ID</th>
            <th className="table-header-text">Customer Name</th>
            <th className="table-header-text">Design Name</th>
            <th className="table-header-text">Color Name</th>
            <th>
              <div className="d-flex flex-column">
                <span className="mb-2 table-header-text">QTY</span>
                <div className="d-flex align-items-center gap-2">
                  <div className="d-flex align-items-center gap-2">
                    <span style={{ fontSize: "11px" }}>Pieces</span>
                    <div className="form-switch d-flex align-items-center">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        checked={quantityView === "sqft"}
                        onChange={() => onQuantityViewChange(quantityView === "pcs" ? "sqft" : "pcs")}
                        style={{ backgroundColor: "#dee2e6" }}
                      />
                    </div>
                    <span style={{ fontSize: "11px" }}>Sqft</span>
                  </div>
                </div>
              </div>
            </th>
            <th className="table-header-text">ORDER & DISPATCH</th>
            <th className="table-header-text" style={{ width: 150 }}>Dispatch Date</th>
            <th className="table-header-text">Progress</th>
            <th className="table-header-text">Preview</th>
          </tr>
        </thead>
        <tbody>
          {products.map((product, index) => {
            const orderId = product.orderId || "-";
            const productId = product.product_id || "-";
            const customerName = product.customerName || "Unknown";
            const designName = product.design_name || "-";
            const colorName = product.color_name || "-";
            const quantityInPieces = Number(product.quantity_in_pieces) || 0;
            const quantityInSoft = Number(product.quantity_in_soft) || 0;
            const orderDate = product.orderDate || "-";
            const tentativeDate = product.tentative_date || "-";
            const progressPercentage = product.progressPercentage || 0;
            const remaining = product.remaining || (quantityInPieces - (Number(product.total_completed_pieces) || 0));

            return (
              <tr key={`${product.product_id}-${index}`}>
                <td className="fw-bold">{orderId}</td>
                <td style={{ cursor: "pointer" }}>
                  <div
                    title={product.prodName}
                    onClick={() => onProductClick(product, orderId)}
                    className="text-primary text-decoration-underline fw-bold"
                  >
                    {productId}
                  </div>
                  <div className="mt-1 d-flex align-items-center gap-1">
                    <Badge
                      pill
                      bg="primary"
                      style={{ cursor: "pointer", fontSize: "0.7rem" }}
                      onClick={() => onAddQueue(
                        { orderId, masterId: product.masterId },
                        productId,
                        product.queue_ids || []
                      )}
                    >
                      Add Queue
                    </Badge>
                    {(product.partial_dispatch_quantity > 0) && (
                      <Badge pill bg="info" style={{ fontSize: "0.7rem" }}>PD</Badge>
                    )}
                  </div>
                </td>
                <td title={customerName}>{truncateText(customerName, 20)}</td>
                <td title={designName}>{truncateText(designName, 15)}</td>
                <td title={colorName}>{truncateText(colorName, 15)}</td>
                <td className="fw-bold">
                  {quantityView === "pcs"
                    ? quantityInPieces.toFixed(0)
                    : quantityInSoft.toFixed(2)}
                </td>
                <td>
                  <div>
                    <strong>
                      {formatDate(orderDate)} & {formatDate(tentativeDate)}
                    </strong>
                    <br />
                    <span className={remaining < 0 ? "text-danger" :
                      remaining === 0 ? "text-success" : "text-warning"}>
                      {renderDateDifference(orderDate, tentativeDate, showCurrentDateDiff)}
                    </span>
                  </div>
                </td>
                <td>
                  <div className="d-flex align-items-center gap-2">
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      style={{ width: 130 }}
                      value={dispatchDatesMap[orderId] || normalizeDateForInput(tentativeDate)}
                      onChange={(e) => onDispatchDateChange(orderId, e.target.value)}
                      disabled={!dispatchDatePermission}
                    />
                    {dispatchDatePermission && (
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => onSaveDispatchDate(
                          orderId,
                          dispatchDatesMap[orderId] || normalizeDateForInput(tentativeDate),
                          product.masterId
                        )}
                        disabled={savingDispatchId === orderId}
                      >
                        {savingDispatchId === orderId ? (
                          <Spinner animation="border" size="sm" />
                        ) : (
                          "Save"
                        )}
                      </button>
                    )}
                  </div>
                </td>
                <td>
                  <ProductProgress
                    product={product}
                    quantityView={quantityView}
                    progressPercentage={progressPercentage}
                  />
                </td>
                <td>
                  <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => onMediaPreview(product, orderId)}
                  >
                    <Icon icon="lucide:image" />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const ProductProgress = ({ product, quantityView, progressPercentage }) => {
  const toNum = (val) => Number(val) || 0;

  const qtyPcs = toNum(product.quantity_in_pieces);
  const partialPcs = toNum(product.partial_dispatch_quantity);
  const completedPcs = toNum(product.total_completed_pieces);
  const totalPcs = qtyPcs + partialPcs;
  const donePcs = completedPcs + partialPcs;
  const percentage = progressPercentage || (totalPcs ? (donePcs * 100) / totalPcs : 0);
  const remaining = qtyPcs - completedPcs;

  const tooltipContent = (
    <>
      Completed: {donePcs} pcs<br />
      {remaining < 0 ? `Extra: +${Math.abs(remaining)}` : `Remaining: ${remaining}`} pcs<br />
      Total: {qtyPcs} pcs
    </>
  );

  return (
    <div className="d-flex flex-column gap-1 w-100" style={{ minWidth: "80px" }}>
      <OverlayTrigger placement="top" overlay={<Tooltip>{tooltipContent}</Tooltip>}>
        <div className="w-100">
          <div className="progress progress-sm rounded-pill">
            <div
              className={`progress-bar rounded-pill ${percentage >= 100 ? "bg-success" : "bg-primary-600"}`}
              style={{ width: `${Math.min(percentage, 100).toFixed(0)}%` }}
            />
          </div>
        </div>
      </OverlayTrigger>
      <OverlayTrigger placement="top" overlay={<Tooltip>{tooltipContent}</Tooltip>}>
        <span className="text-secondary-light font-xs fw-semibold text-center">
          {percentage.toFixed(0)}%
        </span>
      </OverlayTrigger>
    </div>
  );
};

// Helper function
const normalizeDateForInput = (dateString) => {
  if (!dateString || dateString === "-") return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return dateString;

  const parsed = new Date(dateString);
  if (!isNaN(parsed)) {
    return parsed.toISOString().split('T')[0];
  }

  const parts = dateString.match(/^(\d{2})[-\/](\d{2})[-\/](\d{4})$/);
  if (parts) {
    return `${parts[3]}-${parts[2]}-${parts[1]}`;
  }

  return "";
};

export default React.memo(SearchResultsTable);